import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { initIAP, getSubscriptionStatus, restorePurchases } from "../lib/iap";
import { getSessionId } from "../lib/session";
import { api } from "../lib/api";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Preview-only convenience: auto-unlock Pro so the user can test the full app.
// This calls /api/iap/dev/grant which is disabled in production (the endpoint
// returns 403 when REVENUECAT_WEBHOOK_SECRET is set).
const PREVIEW_HOSTS = ["preview.emergentagent.com", "localhost", "127.0.0.1"];
const isPreviewEnv = () => {
  try { return PREVIEW_HOSTS.some((h) => window.location.hostname.includes(h)); }
  catch { return false; }
};

/**
 * Preview override: add `?paywall=1` to the URL to see the paywall in preview
 * (skip the auto-grant). `?paywall=0` clears it. Persists in sessionStorage so
 * the flag survives navigation within the same tab.
 */
const PAYWALL_TEST_KEY = "fff-paywall-test-mode";
const readPaywallOverride = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("paywall")) {
      const v = params.get("paywall");
      if (v === "0" || v === "off" || v === "false") {
        sessionStorage.removeItem(PAYWALL_TEST_KEY);
        return false;
      }
      sessionStorage.setItem(PAYWALL_TEST_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(PAYWALL_TEST_KEY) === "1";
  } catch { return false; }
};

const SubscriptionContext = createContext({
  loading: true,
  isPro: false,
  refresh: async () => {},
  restore: async () => false,
});

export const SubscriptionProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState(false);

  const refresh = useCallback(async () => {
    // Preview override: `?paywall=1` forces isPro=false so we can test the paywall.
    const paywallTest = readPaywallOverride();

    // Local check (RevenueCat native / web simulator)
    const localActive = paywallTest ? false : await getSubscriptionStatus();
    // Server check (RevenueCat webhook mirrored into Mongo) — defence in depth
    let serverActive = false;
    if (!paywallTest) {
      try {
        const s = await api.iapStatus();
        serverActive = !!s?.active;
      } catch { /* server unreachable — ignore, fall back to local */ }
    }

    // Auto-grant Pro on preview if not already active so the team can test.
    // Skipped when the tester is explicitly testing the paywall.
    if (!paywallTest && !localActive && !serverActive && isPreviewEnv()) {
      try {
        await axios.post(`${BACKEND_URL}/api/iap/dev/grant`, null, {
          params: { app_user_id: getSessionId(), active: true },
        });
        serverActive = true;
      } catch { /* dev/grant disabled in production — ignore */ }
    }

    const active = paywallTest ? false : (localActive || serverActive);
    setIsPro(active);
    setLoading(false);
    return active;
  }, []);

  const restore = useCallback(async () => {
    const active = await restorePurchases();
    setIsPro(active);
    return active;
  }, []);

  useEffect(() => {
    (async () => {
      await initIAP(getSessionId());
      await refresh();
    })();
  }, [refresh]);

  return (
    <SubscriptionContext.Provider value={{ loading, isPro, refresh, restore, setIsPro }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
